import { motion } from "framer-motion";
import { Star, Trophy } from "lucide-react";
import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";

interface SeasonCard {
	id: string;
	season: string;
	tier: string;
	points: string;
	interactions: number;
	textColor: string;
	accentBg: string;
	accentBorder: string;
	border: string;
	shadow: string;
	image: string;
}

const SEASONS: SeasonCard[] = [
	{
		id: "s1",
		season: "Genesis Season",
		tier: "Graphite",
		points: "15,420",
		interactions: 124,
		textColor: "text-slate-400",
		accentBg: "bg-slate-400/10",
		accentBorder: "border-slate-400/20",
		border: "border-slate-400/20",
		shadow: "shadow-slate-400/10",
		image: "/images/Graphite-kai-card.png",
	},
	{
		id: "s2",
		season: "Season Alpha",
		tier: "Chromium",
		points: "8,250",
		interactions: 89,
		textColor: "text-cyan-400",
		accentBg: "bg-cyan-400/10",
		accentBorder: "border-cyan-400/20",
		border: "border-cyan-400/20",
		shadow: "shadow-cyan-400/10",
		image: "/images/Chromium-kai-card.png",
	},
	{
		id: "s3",
		season: "Season Beta",
		tier: "Silicon",
		points: "4,100",
		interactions: 42,
		textColor: "text-indigo-400",
		accentBg: "bg-indigo-400/10",
		accentBorder: "border-indigo-400/20",
		border: "border-indigo-400/20",
		shadow: "shadow-indigo-400/10",
		image: "/images/Silicon-kai-card.png.png",
	},
	{
		id: "s4",
		season: "Season Gamma",
		tier: "Diamond",
		points: "2,050",
		interactions: 15,
		textColor: "text-blue-400",
		accentBg: "bg-blue-400/10",
		accentBorder: "border-blue-400/20",
		border: "border-blue-400/20",
		shadow: "shadow-blue-400/10",
		image: "/images/Diamond-kai-card.png.png",
	},
	{
		id: "s5",
		season: "Season Delta",
		tier: "Obsidian",
		points: "950",
		interactions: 8,
		textColor: "text-zinc-400",
		accentBg: "bg-zinc-500/10",
		accentBorder: "border-zinc-500/20",
		border: "border-zinc-500/20",
		shadow: "shadow-zinc-500/10",
		image: "/images/Obsidian-kai-card.png.png",
	},
];

export default function SeasonCardDeck() {
	const [cards, setCards] = useState(SEASONS);

	useEffect(() => {
		const shuffled = [...SEASONS].sort(() => Math.random() - 0.5);
		setCards(shuffled);
	}, []);

	return (
		<div className="relative w-full h-[400px] flex items-center justify-center perspective-[1000px]">
			{/* Background Glow */}
			<div className="absolute inset-0 flex items-center justify-center pointer-events-none">
				<div className="w-64 h-64 bg-brand/5 rounded-full blur-[80px] mix-blend-screen animate-pulse" />
			</div>

			<div className="relative w-64 h-96 group">
				{cards.map((card, index) => {
					// Calculate fan position (original animation math)
					const rotation = index * 5 - 10; // -10, -5, 0, 5, 10
					const xOffset = index * 30 - 60; // Spread horizontally
					const yOffset = Math.abs(index - 2) * 5; // Slight arch

					return (
						<motion.div
							key={card.id}
							initial={{
								x: index % 2 === 0 ? -100 : 100,
								y: 100,
								rotate: 0,
								opacity: 0,
							}}
							animate={{
								rotate: rotation,
								x: xOffset,
								y: yOffset,
								scale: 1,
								opacity: 1,
								zIndex: index,
							}}
							whileHover={{
								y: yOffset - 20,
								scale: 1.05,
								transition: { duration: 0.2 },
							}}
							transition={{
								type: "spring",
								stiffness: 260,
								damping: 20,
							}}
							className="absolute top-0 left-0 w-64 h-96 cursor-pointer origin-bottom"
						>
							{/* --- Card Outer Frame (QuestCard style) --- */}
							<div
								className={cn(
									"h-full rounded-[18px] p-2 bg-[#121214] border shadow-2xl",
									card.border,
									card.shadow,
								)}
							>
								{/* Card Inner Frame */}
								<div className="relative flex flex-col h-full rounded-[12px] overflow-hidden bg-[#18181b] border border-white/5">
									{/* Noise Texture */}
									<div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-[0.03] pointer-events-none z-0" />

									{/* --- Header Bar --- */}
									<div className="relative z-10 flex items-center justify-between gap-2 min-h-9 px-3 py-1.5 bg-[#202023] border-b border-white/5">
										<span className="text-[10px] font-medium text-text-default truncate flex-1 min-w-0 leading-snug">
											{card.season}
										</span>
										{/* Points Badge */}
										<div className="flex items-center gap-1 h-6 px-2 rounded-lg border border-brand/20 shrink-0">
											<Star className="w-2.5 h-2.5 fill-brand text-brand" />
											<span className="text-[10px] font-extrabold text-brand tabular-nums">
												{card.points}
											</span>
											<span className="text-[7px] font-bold text-text-secondary uppercase ml-0.5">
												pts
											</span>
										</div>
									</div>

									{/* --- Card Art --- */}
									<div className="relative flex-1 bg-black/40 overflow-hidden border-b border-white/5">
										<img
											src={card.image}
											alt={card.tier}
											className="w-full h-full object-contain transition-transform duration-500 hover:scale-110"
										/>
									</div>

									{/* --- Card Body --- */}
									<div className="relative z-10 p-3 space-y-2">
										{/* Type Line */}
										<div className="flex items-center gap-1.5 pb-1.5 border-b border-dashed border-white/5">
											<span className="text-[8px] uppercase tracking-widest font-bold text-text-tertiary">
												Season Card
											</span>
											<div className="h-px flex-1 bg-white/5" />
										</div>

										{/* Tier & Stats */}
										<div className="flex items-center justify-between">
											<div
												className={cn(
													"flex items-center gap-1.5 px-2 py-1 rounded-md border text-[10px] font-bold uppercase tracking-wider",
													card.accentBg,
													card.accentBorder,
													card.textColor,
												)}
											>
												<Trophy className="w-3 h-3" />
												{card.tier}
											</div>
											<div className="text-right">
												<div className="text-[8px] uppercase tracking-wider text-text-tertiary font-semibold">
													Actions
												</div>
												<div className="text-xs font-mono font-bold text-text-default tabular-nums">
													{card.interactions}
												</div>
											</div>
										</div>

										{/* Serial */}
										<div className="flex items-center gap-1.5 pt-1 opacity-40">
											<div
												className={cn(
													"h-1.5 w-1.5 rounded-full animate-pulse",
													card.textColor.replace("text-", "bg-"),
												)}
											/>
											<span className="text-[7px] font-mono tracking-widest text-text-tertiary uppercase">
												Shinkai-{card.id.toUpperCase()}-8823
											</span>
										</div>
									</div>
								</div>
							</div>
						</motion.div>
					);
				})}
			</div>

			{/* Label */}
			<div className="absolute -bottom-8 left-0 right-0 text-center">
				<p className="text-sm text-text-secondary font-medium">
					Collect rewards across seasons
				</p>
			</div>
		</div>
	);
}
